/**
 * Desktop/mobile device-group annotation for the email editor.
 *
 * Production emails express "this content only renders on one form factor" in
 * many unrelated vocabularies (`mobile-only`/`desktop-only` divs,
 * `ht-mobile-hide`/`ht-desktop-hide` tables, bare `.desktop`/`.mobile` cells,
 * one-sided `hide-mobile` elements, ...). None of them carries a
 * machine-readable link between a block and its counterpart, so structural
 * edits (drag, delete) that operate on single DOM nodes silently split pairs.
 *
 * This module runs ONE normalization pass when a document enters the editor:
 * it resolves each element's *effective CSS visibility* at a desktop and a
 * mobile viewport width (evaluating the document's own `@media` rules, so it
 * works across vocabularies without class-name matching), clusters
 * complementary adjacent siblings into device groups, and stamps
 * `data-ht-group` + `data-ht-device` attributes. Structural edit operations
 * then consume the annotations as fact instead of re-guessing at edit time.
 * One-sided hides (an element hidden on one view with no counterpart) get a
 * device annotation but no group, so the editor can badge them rather than
 * glue an unrelated sibling to them.
 *
 * Email clients ignore `data-*` attributes, so the annotations are safe to
 * persist in saved HTML; the pass is idempotent and respects annotations
 * already present (whether stamped by a previous pass or emitted natively by
 * our own template exports).
 */

import {
  formatGroupId,
  parseGroupIdNumber,
  DEVICE_ATTR,
  DEVICE_GROUP_ATTR,
  type DeviceKind,
  type GroupedDevice,
} from "@hightouch/lib/llm/lifecycle-studio/responsive-annotation/device-attributes";

import { MOBILE_PREVIEW_WIDTH_PX } from "src/pages/lifecycle-studio/components/email-preview-constants";

// Wire contract shared with the backend responsive-annotation applier — defined
// once in @hightouch/lib, re-exported here for the editor's structural-edit modules.
export { DEVICE_ATTR, DEVICE_GROUP_ATTR };
export type { DeviceKind, GroupedDevice };

// The pre-rename spelling of `DEVICE_ATTR`. Stored emails annotated before the
// rename (and HTML from writers not yet updated) still carry it;
// `annotateDeviceGroups` rewrites it in place on load and the rename persists
// on the next save.
// TODO: remove when done testing in prod
const LEGACY_DEVICE_ATTR = "data-ht-variant";

// Widths the stylesheets resolve at, defining what "mobile"/"desktop" mean.
// Mobile = the preview mobile canvas width. Desktop has no fixed width, so it
// must sit above every breakpoint an email can carry (brand profiles allow a
// mobile breakpoint up to 1024px, MAX_MOBILE_BREAKPOINT_WIDTH), hence 1100px.
export const DESKTOP_EVAL_WIDTH_PX = 1100;
export const MOBILE_EVAL_WIDTH_PX = MOBILE_PREVIEW_WIDTH_PX;

// Editor-injected stylesheets that must not participate in visibility
// resolution. `ht-editable-style` is the selection/hover-outline sheet
// injected by use-iframe-interactivity on every document init — outlines and
// cursors only, it never sets `display`, and it's stripped before
// serialization. The other editor-injected sheet, `ht-canonical-style` (the
// render-only responsive block for component emails), is deliberately NOT
// listed: component fragments are saved headless with their show/hide media
// rules living in that sheet, so excluding it would blind device detection
// for exactly the documents that need it.
const EDITOR_STYLE_ELEMENT_IDS = new Set(["ht-editable-style"]);

type DisplayDeclaration = {
  /** null = top-level rule (applies at every width). */
  mediaCondition: string | null;
  selector: string;
  value: string;
  important: boolean;
};

/**
 * CSS comments can contain braces, semicolons, or entire commented-out rules,
 * any of which would corrupt the brace-matching scan below — so they're
 * removed before anything else looks at the CSS.
 */
export const stripCssComments = (css: string): string =>
  css.replace(/\/\*[\s\S]*?\*\//g, "");

/**
 * Returns the index of the `}` closing the `{` at `openIndex`, or -1 when
 * unbalanced. Counts nesting depth so a block containing inner blocks (an
 * `@media` body full of rules) is consumed as one unit — this is what lets
 * the scanner treat CSS as `prelude { body }` chunks without a real parser.
 */
export const findBlockEnd = (css: string, openIndex: number): number => {
  let depth = 0;
  for (let i = openIndex; i < css.length; i++) {
    if (css[i] === "{") depth++;
    else if (css[i] === "}") {
      depth--;
      if (depth === 0) return i;
    }
  }
  return -1;
};

/**
 * Pulls the winning `display` value out of a declaration block's body (the
 * text between `{` and `}`). Splits on `;`, keeps only `display:` declarations,
 * and peels off `!important` so the cascade tiers in
 * {@link resolveDisplayAtWidth} can weigh it. Returns null when the block
 * doesn't set `display` at all — such rules are irrelevant to visibility and
 * are dropped at the source.
 */
const parseDisplayFromDeclarations = (
  declarations: string,
): { value: string; important: boolean } | null => {
  // Last display declaration in the block wins.
  let result: { value: string; important: boolean } | null = null;
  for (const decl of declarations.split(";")) {
    const colon = decl.indexOf(":");
    if (colon === -1) continue;
    if (decl.slice(0, colon).trim().toLowerCase() !== "display") continue;
    let value = decl.slice(colon + 1).trim();
    const important = /!\s*important\s*$/i.test(value);
    if (important) value = value.replace(/!\s*important\s*$/i, "").trim();
    result = { value: value.toLowerCase(), important };
  }
  return result;
};

/**
 * Scans the *body* of an `@media` block for `selector { declarations }` rules
 * and appends their display declarations to `out`, each tagged with the
 * enclosing media condition. Exists because rules inside a media block need
 * that condition attached — the top-level walk in
 * {@link extractDisplayDeclarations} hands the whole body here after peeling
 * the `@media` prelude off. Same chunking as the top-level walk: find `{`,
 * brace-match to the closing `}`, treat the text before the brace as the
 * selector list.
 */
const parseRuleBlock = (
  body: string,
  mediaCondition: string | null,
  out: DisplayDeclaration[],
): void => {
  let i = 0;
  while (i < body.length) {
    const open = body.indexOf("{", i);
    if (open === -1) break;
    const close = findBlockEnd(body, open);
    if (close === -1) break;
    const selectorText = body.slice(i, open).trim();
    const declarations = body.slice(open + 1, close);
    // Nested at-rules inside a rule body would have been consumed by
    // findBlockEnd; selectors starting with @ (e.g. @font-face) carry no
    // element visibility.
    if (selectorText && !selectorText.startsWith("@")) {
      const display = parseDisplayFromDeclarations(declarations);
      if (display) {
        for (const selector of selectorText.split(",")) {
          const trimmed = selector.trim();
          if (trimmed) {
            out.push({ mediaCondition, selector: trimmed, ...display });
          }
        }
      }
    }
    i = close + 1;
  }
};

/**
 * Extracts every `display` declaration (with its selector and enclosing
 * `@media` condition) from raw CSS, in document order. A tiny purpose-built
 * scan rather than a full CSS parser: only `display` affects visibility, and
 * email CSS is simple enough that balanced-brace extraction is reliable — the
 * same approach the backend's media-query scorers use.
 *
 * Mechanics: after comment-stripping, walk the text as `prelude { body }`
 * chunks (brace-matched via {@link findBlockEnd}). An `@media` prelude hands
 * its body to {@link parseRuleBlock} with the condition attached; other
 * at-rules are skipped whole; a plain prelude is a selector list whose body
 * is mined directly via {@link parseDisplayFromDeclarations}.
 */
export const extractDisplayDeclarations = (
  css: string,
): DisplayDeclaration[] => {
  const out: DisplayDeclaration[] = [];
  const text = stripCssComments(css);
  let i = 0;
  while (i < text.length) {
    const open = text.indexOf("{", i);
    if (open === -1) break;
    const prelude = text.slice(i, open).trim();
    const close = findBlockEnd(text, open);
    if (close === -1) break;
    const body = text.slice(open + 1, close);
    if (/^@media\b/i.test(prelude)) {
      const condition = prelude.replace(/^@media\b/i, "").trim();
      parseRuleBlock(body, condition, out);
    } else if (prelude.startsWith("@")) {
      // Other at-rules (@supports, @keyframes, @font-face…) — skip the block.
    } else if (prelude) {
      const display = parseDisplayFromDeclarations(body);
      if (display) {
        for (const selector of prelude.split(",")) {
          const trimmed = selector.trim();
          if (trimmed) {
            out.push({ mediaCondition: null, selector: trimmed, ...display });
          }
        }
      }
    }
    i = close + 1;
  }
  return out;
};

/**
 * Whether a media query list applies at the given viewport width.
 *
 * Only width-determinable queries are honored: each comma-separated query must
 * consist of neutral media types (`screen`/`all`/`only`) and
 * `min/max-(device-)width` clauses. Queries carrying any other feature
 * (`prefers-color-scheme`, resolution, orientation…) or a `not` are treated as
 * not applying — visibility must never be derived from conditions we can't
 * evaluate, and dark-mode/DPI queries don't express form-factor splits.
 */
export const mediaConditionAppliesAtWidth = (
  condition: string,
  widthPx: number,
): boolean => {
  return condition.split(",").some((query) => {
    const q = query.trim().toLowerCase();
    if (!q || q.includes("not ")) return false;

    // Every parenthesized clause must be a satisfiable width clause.
    const clauses = q.match(/\([^)]*\)/g) ?? [];
    // Remainder after removing clauses must be only neutral keywords.
    const remainder = q
      .replace(/\([^)]*\)/g, " ")
      .replace(/\b(only|and|screen|all)\b/g, " ")
      .trim();
    if (remainder) return false;

    return clauses.every((clause) => {
      const m = clause.match(
        /^\(\s*(min|max)-(?:device-)?width\s*:\s*([\d.]+)px\s*\)$/,
      );
      if (!m || m[2] === undefined) return false;
      const bound = parseFloat(m[2]);
      return m[1] === "min" ? widthPx >= bound : widthPx <= bound;
    });
  });
};

type ElementDisplayState = {
  // Cascade-resolved display, tracked separately per priority tier: a later
  // rule of the same tier overrides, `!important` beats inline, inline beats
  // plain rules. Selector specificity is deliberately ignored — email
  // show/hide CSS relies on `!important` and simple class selectors, and a
  // full cascade isn't worth the complexity here.
  normal?: string;
  important?: string;
};

const resolveDisplayAtWidth = (
  root: Element,
  declarations: DisplayDeclaration[],
  widthPx: number,
): Map<Element, string> => {
  const states = new Map<Element, ElementDisplayState>();
  for (const decl of declarations) {
    if (
      decl.mediaCondition !== null &&
      !mediaConditionAppliesAtWidth(decl.mediaCondition, widthPx)
    ) {
      continue;
    }
    let matches: Iterable<Element>;
    try {
      matches = root.querySelectorAll(decl.selector);
    } catch {
      // Selector syntax the DOM engine rejects — skip it.
      continue;
    }
    for (const el of matches) {
      const state = states.get(el) ?? {};
      if (decl.important) state.important = decl.value;
      else state.normal = decl.value;
      states.set(el, state);
    }
  }

  const resolved = new Map<Element, string>();
  for (const [el, state] of states) {
    const inline = (el as HTMLElement).style?.display || undefined;
    const display = state.important ?? inline ?? state.normal;
    if (display) resolved.set(el, display);
  }
  return resolved;
};

const isHidden = (el: Element, resolved: Map<Element, string>): boolean => {
  const fromRules = resolved.get(el);
  if (fromRules !== undefined) return fromRules === "none";
  return (el as HTMLElement).style?.display === "none";
};

export type DeviceVisibility = {
  /** Hidden by its own display value at the desktop eval width. */
  hiddenDesktop: boolean;
  /** Hidden by its own display value at the mobile eval width. */
  hiddenMobile: boolean;
};

/**
 * Resolves each element's own (not inherited) display at both eval widths.
 * Exposed for tests; `annotateDeviceGroups` is the entry point.
 * A style `canReadStyle` rejects is left out of the resolution.
 */
export const computeDeviceVisibility = (
  doc: Document,
  canReadStyle?: (style: HTMLStyleElement) => boolean,
): Map<Element, DeviceVisibility> => {
  const body = doc.body;
  if (!body) return new Map();

  const cssTexts: string[] = [];
  for (const style of doc.querySelectorAll("style")) {
    if (EDITOR_STYLE_ELEMENT_IDS.has(style.id)) continue;
    if (canReadStyle && !canReadStyle(style)) continue;
    cssTexts.push(style.textContent ?? "");
  }
  const declarations = extractDisplayDeclarations(cssTexts.join("\n"));

  const desktop = resolveDisplayAtWidth(
    body,
    declarations,
    DESKTOP_EVAL_WIDTH_PX,
  );
  const mobile = resolveDisplayAtWidth(
    body,
    declarations,
    MOBILE_EVAL_WIDTH_PX,
  );

  const result = new Map<Element, DeviceVisibility>();
  for (const el of body.querySelectorAll("*")) {
    result.set(el, {
      hiddenDesktop: isHidden(el, desktop),
      hiddenMobile: isHidden(el, mobile),
    });
  }
  return result;
};

const deviceKindOf = (
  v: DeviceVisibility | undefined,
): "desktop" | "mobile" | null => {
  if (!v) return null;
  // "desktop" = renders on desktop only (hidden on mobile), and vice versa.
  if (v.hiddenMobile && !v.hiddenDesktop) return "desktop";
  if (v.hiddenDesktop && !v.hiddenMobile) return "mobile";
  return null;
};

// The `g_<n>` id namespace is shared with the backend import-time pipeline and
// the content agent's prompt contract, so this pass continues one document-wide
// sequence instead of starting its own.
const nextGroupNumber = (doc: Document): number => {
  let max = 0;
  for (const el of doc.querySelectorAll(`[${DEVICE_GROUP_ATTR}]`)) {
    const n = parseGroupIdNumber(el.getAttribute(DEVICE_GROUP_ATTR) ?? "");
    if (n !== null) max = Math.max(max, n);
  }
  return max + 1;
};

/**
 * Rewrite legacy `data-ht-variant` marks to `data-ht-device` in place. Runs
 * before boundary detection so a pre-rename annotation is trusted as an
 * existing annotation rather than re-derived; an element somehow carrying both
 * spellings keeps its `data-ht-device` value.
 * TODO: remove when done testing in prod
 */
const migrateLegacyDeviceAttributes = (doc: Document): void => {
  for (const el of doc.querySelectorAll(`[${LEGACY_DEVICE_ATTR}]`)) {
    const value = el.getAttribute(LEGACY_DEVICE_ATTR);
    el.removeAttribute(LEGACY_DEVICE_ATTR);
    if (value && !el.hasAttribute(DEVICE_ATTR)) {
      el.setAttribute(DEVICE_ATTR, value);
    }
  }
};

export type AnnotateResult = {
  /** Number of new device groups (pairs) stamped by this pass. */
  groups: number;
  /** Number of new one-sided (`*-only`) annotations stamped by this pass. */
  oneSided: number;
};

/**
 * The Solution-1 normalization pass: detect device boundaries via effective
 * CSS visibility, pair complementary adjacent siblings, and stamp
 * `data-ht-group` / `data-ht-device`.
 *
 * A "boundary" is the topmost element whose own display differs between the
 * two widths; descendants of a boundary are skipped (moving the boundary moves
 * them), while pairs nested inside shared blocks (e.g. paired CTAs) are still
 * detected because their block parent is not itself a boundary.
 *
 * Idempotent: elements already carrying `data-ht-device` are trusted as-is,
 * so a document annotated by a previous pass (or natively by our exports)
 * round-trips unchanged. Legacy `data-ht-variant` annotations are rewritten to
 * `data-ht-device` first, so pre-rename documents are trusted the same way
 * rather than re-derived.
 *
 * `canReadStyle` leaves out styles whose rules must not stamp the email's own elements.
 */
export const annotateDeviceGroups = (
  doc: Document,
  canReadStyle?: (style: HTMLStyleElement) => boolean,
): AnnotateResult => {
  const result: AnnotateResult = { groups: 0, oneSided: 0 };
  const body = doc.body;
  if (!body) return result;

  migrateLegacyDeviceAttributes(doc);

  const visibility = computeDeviceVisibility(doc, canReadStyle);

  // Topmost boundaries in document order.
  const boundaries: Element[] = [];
  const collect = (el: Element) => {
    if (deviceKindOf(visibility.get(el))) {
      boundaries.push(el);
      return; // don't descend — nested differences ride along with this node
    }
    for (const child of el.children) collect(child);
  };
  for (const child of body.children) collect(child);

  let groupNumber = nextGroupNumber(doc);
  const grouped = new Set<Element>();

  // Pair complementary boundaries that are directly adjacent element siblings
  // (whitespace and comment nodes between them don't break adjacency).
  for (let i = 0; i < boundaries.length - 1; i++) {
    const a = boundaries[i];
    const b = boundaries[i + 1];
    if (!a || !b || grouped.has(a) || grouped.has(b)) continue;
    if (a.nextElementSibling !== b) continue;
    const kindA = deviceKindOf(visibility.get(a));
    const kindB = deviceKindOf(visibility.get(b));
    if (!kindA || !kindB || kindA === kindB) continue;
    // Respect existing annotations rather than re-deriving over them.
    if (a.hasAttribute(DEVICE_ATTR) || b.hasAttribute(DEVICE_ATTR)) continue;

    const groupId = formatGroupId(groupNumber++);
    a.setAttribute(DEVICE_GROUP_ATTR, groupId);
    a.setAttribute(DEVICE_ATTR, kindA);
    b.setAttribute(DEVICE_GROUP_ATTR, groupId);
    b.setAttribute(DEVICE_ATTR, kindB);
    grouped.add(a);
    grouped.add(b);
    result.groups++;
  }

  // Remaining boundaries are one-sided hides: annotate their per-view
  // visibility, but never invent a partner for them.
  for (const el of boundaries) {
    if (grouped.has(el) || el.hasAttribute(DEVICE_ATTR)) continue;
    const kind = deviceKindOf(visibility.get(el));
    if (!kind) continue;
    el.setAttribute(DEVICE_ATTR, `${kind}-only`);
    result.oneSided++;
  }

  return result;
};

/**
 * The other half of `el`'s device group, or null when `el` isn't part of a
 * group. Consumed by structural edit operations (drag, delete) so both halves
 * of a pair travel as one unit.
 */
export const findDevicePartner = (el: Element): HTMLElement | null => {
  const groupId = el.getAttribute(DEVICE_GROUP_ATTR);
  if (!groupId) return null;
  const doc = el.ownerDocument;
  if (!doc) return null;
  for (const candidate of doc.querySelectorAll(`[${DEVICE_GROUP_ATTR}]`)) {
    if (
      candidate !== el &&
      candidate.getAttribute(DEVICE_GROUP_ATTR) === groupId
    ) {
      return candidate as HTMLElement;
    }
  }
  return null;
};

/**
 * Whether `el` still renders once the preview switches to `device`, per the
 * nearest device annotation at or above it. Unannotated elements render on both
 * form factors; an unrecognized annotation value is treated the same way rather
 * than hiding content on a guess.
 */
export const isRenderedOnDevice = (
  el: Element | null,
  device: GroupedDevice,
): boolean => {
  const kind = el?.closest(`[${DEVICE_ATTR}]`)?.getAttribute(DEVICE_ATTR);
  if (!kind) return true;
  const hiddenOn: GroupedDevice = device === "desktop" ? "mobile" : "desktop";
  return kind !== hiddenOn && kind !== `${hiddenOn}-only`;
};

/**
 * Splits `elements` by whether the `device` preview still renders them,
 * preserving order within each half. Consumed by the device switch to prune a
 * multi-selection: members are independent elements, so a shared block can stay
 * visible while a paired one next to it disappears.
 */
export const partitionByDeviceVisibility = (
  elements: readonly Element[],
  device: GroupedDevice,
): { rendered: Element[]; hidden: Element[] } => {
  const rendered: Element[] = [];
  const hidden: Element[] = [];
  for (const el of elements) {
    if (isRenderedOnDevice(el, device)) {
      rendered.push(el);
    } else {
      hidden.push(el);
    }
  }
  return { rendered, hidden };
};

/**
 * The form factor of the counterpart that edits to `el` will NOT reach, or null
 * when there is none. Unlike `findDevicePartner`, this walks up from `el` to the
 * nearest annotated ancestor: groups are stamped on the boundary block, while a
 * selection is usually a descendant of it (the `<p>` inside a paired cell).
 *
 * One-sided hides (`*-only`, no group) return null — there is no counterpart to
 * fall out of sync, so there is nothing to warn about.
 */
export const findCounterpartDevice = (
  el: Element | null,
): GroupedDevice | null => {
  const annotated = el?.closest(`[${DEVICE_GROUP_ATTR}]`);
  if (!annotated) return null;
  const partner = findDevicePartner(annotated);
  const kind = partner?.getAttribute(DEVICE_ATTR);
  return kind === "desktop" || kind === "mobile" ? kind : null;
};
